import { Component, computed, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessagesModule } from 'primeng/messages';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { SegmentFiltersPreviewComponent } from '@shared/components/segment-filters-preview/segment-filters-preview.component';

import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { ConditionalSegmentNode } from '@modules/campaigns/Domain/Nodes/ConditionalNodeSegment';
import { ConditionalSegmentConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { SegmentationService } from '@modules/segments/services/segments.service';
import { Segment } from '@modules/segments/models/segmentation.model';
import { ActivatedRoute } from '@angular/router';
import { ValidationError } from '@modules/campaigns/Domain/Enums/ValidationErrors';

@Component({
  selector: 'app-segment-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    MessagesModule,
    CardModule,
    ButtonModule,
    DividerModule,
    LucideIconsModule,
    TooltipModule,
    DropdownModule,
    SegmentFiltersPreviewComponent,
  ],
  templateUrl: './segment-inspector.component.html',
  styleUrls: ['./segment-inspector.component.scss'],
})
export class SegmentInspectorComponent extends InspectorBase<ConditionalSegmentNode> {
  segments = signal<Segment[]>([]);
  isLoading = signal<boolean>(false);
  selectedSegmentId = signal<string | null>(null);
  collectors = signal<any[]>([]);
  portfolios = signal<any[]>([]);

  selectedSegment = computed(() => {
    const id = this.selectedSegmentId();
    return this.segments().find((s) => s.id === id) ?? null;
  });

  editSegmentUrl = computed(() => {
    const templateId = this.selectedSegmentId();
    if (!templateId) return null;

    const tenantId = this.route.snapshot.queryParams['tenant'];
    return `/segments/?tenant=${tenantId}`;
  });

  constructor(
    private workflowState: WorkflowStateService,
    private translate: TranslateService,
    private segmentationService: SegmentationService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  override get nodeValue(): ConditionalSegmentNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.ConditionalSegments
      ? (current as ConditionalSegmentNode)
      : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SEGMENT.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SEGMENT.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  private loadOptions() {
    this.isLoading.set(true);
    this.segmentationService.getSegments().subscribe((list: Segment[]) => {
      this.segments.set(list || []);
      this.isLoading.set(false);
    });

    const routeTenantCollectors = (this as any)?.campaignContext?.campaign?.collectors ?? [];
    if (Array.isArray(routeTenantCollectors)) this.collectors.set(routeTenantCollectors);
  }

  onSelectSegment(event: any | string) {
    const selectedId = typeof event === 'string' ? event : event?.value ?? null;
    this.selectedSegmentId.set(selectedId);
    this.scheduleApplyChanges();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    // Always get the latest node from workflow state
    const currentNodes = this.workflowState.nodes();
    const currentNode = this.node();
    const node = currentNodes.find((n) => n.id === currentNode?.id) as ConditionalSegmentNode;

    if (!node) return;

    const baseConfig = (node.config as ConditionalSegmentConfig) ?? {};
    const selectedId = this.selectedSegmentId();

    const updatedConfig: ConditionalSegmentConfig = {
      ...baseConfig,
      segmentIds: selectedId ? [selectedId] : [],
    } as ConditionalSegmentConfig;

    const updated = node.cloneWithConfig(updatedConfig) as ConditionalSegmentNode;
    const validation = updated.validate();
    this.errors.set(validation);
    const shouldPersist =
      validation.length === 0 ||
      validation.every((error) => error.errorMessage === ValidationError.NO_CASE_CONDITIONAL);

    if (shouldPersist) {
      this.workflowState.addOrUpdateNode(updated);
      this.node.set(updated);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as ConditionalSegmentConfig;
    const id = cfg.segmentIds?.[0] ?? null;
    this.selectedSegmentId.set(id);

    this.errors.set([]);
  }

  override setNode(nodeSignal: any) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
    this.loadOptions();
  }
}
